/**
 * Settings store.
 *
 * Persisted user preferences. Phase 1 defines the shape and defaults; the
 * Settings page binds its controls to these values. Feature-specific settings
 * (model parameters, storage paths) will extend this as they arrive.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  DEFAULT_CHAT_PARAMS,
  DEFAULT_SENSITIVE_PATTERNS,
  DEFAULT_WORKSPACE_ID,
  type ChatParams,
} from "@/types";

/** How the app picks a starting point on launch. */
export type StartupBehavior = "last-workspace" | "new-chat";

interface SettingsState {
  // General
  launchOnStartup: boolean;
  restoreLastSession: boolean;

  // Workspace
  /** The workspace every workspace-scoped store filters/creates against. */
  activeWorkspaceId: string;

  // Appearance
  reduceMotion: boolean;
  compactDensity: boolean;
  showStatusBar: boolean;

  // Models
  /** Id of the model new conversations start with. */
  defaultModelId: string | null;
  /** Generation defaults new conversations are seeded with. */
  defaultChatParams: ChatParams;
  /** Ollama model used to embed Knowledge documents and chat queries — e.g.
   * "nomic-embed-text". `null` until the user picks one on the Models page. */
  embeddingModelId: string | null;

  // Storage
  /** Base URL of the local Ollama server. */
  ollamaHost: string;
  /** Pinned absolute paths shown as Favorites in the File Browser. */
  favoriteFilePaths: string[];

  // Updates
  autoCheckUpdates: boolean;

  // Advanced
  developerMode: boolean;

  // Privacy
  telemetryEnabled: boolean;
  /** Opt-in gate for the Clipboard Intelligence background monitor — off by
   * default, per "clipboard must be monitored only with explicit
   * permission." */
  clipboardMonitoringEnabled: boolean;
  clipboardAutoCleanEnabled: boolean;
  /** Regex source strings checked against clipboard content before it's
   * stored; a match skips storing the item entirely. */
  clipboardSensitivePatterns: string[];

  // Personalization
  defaultPromptId: string | null;
  language: string;
  startupBehavior: StartupBehavior;

  // Voice
  voiceInputMode: "push-to-talk" | "continuous";
  pushToTalkHotkey: string;
  preferredMicDeviceId: string | null;

  set: <K extends keyof SettingsValues>(key: K, value: SettingsValues[K]) => void;
  reset: () => void;
}

/** The persisted value slice, without the actions. */
type SettingsValues = Omit<SettingsState, "set" | "reset">;

const DEFAULTS: SettingsValues = {
  launchOnStartup: false,
  restoreLastSession: true,
  activeWorkspaceId: DEFAULT_WORKSPACE_ID,
  reduceMotion: false,
  compactDensity: false,
  showStatusBar: true,
  defaultModelId: null,
  defaultChatParams: DEFAULT_CHAT_PARAMS,
  embeddingModelId: null,
  ollamaHost: "http://127.0.0.1:11434",
  favoriteFilePaths: [],
  autoCheckUpdates: true,
  developerMode: import.meta.env.DEV,
  telemetryEnabled: false,
  clipboardMonitoringEnabled: false,
  clipboardAutoCleanEnabled: true,
  clipboardSensitivePatterns: DEFAULT_SENSITIVE_PATTERNS,
  defaultPromptId: null,
  language: "en",
  startupBehavior: "last-workspace",
  voiceInputMode: "push-to-talk",
  pushToTalkHotkey: "mod+shift+v",
  preferredMicDeviceId: null,
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      set: (key, value) => set({ [key]: value } as Partial<SettingsState>),
      reset: () => set({ ...DEFAULTS }),
    }),
    {
      name: "seufic.settings",
      storage: createJSONStorage(() => localStorage),
      version: 3,
      migrate: (persisted) => ({ ...DEFAULTS, ...(persisted as Partial<SettingsValues>) }),
    },
  ),
);
